export interface EmailBlock {
  type: 'text' | 'quote' | 'separator' | 'header' | 'image-marker' | 'signature'
  content: string
  level: number // nivel de citas anidadas
  metadata?: Record<string, string>
}

/**
 * Detecta si el texto es HTML puro
 */
export function isHtml(text: string | null | undefined): boolean {
  if (!text || typeof text !== 'string') return false
  return /<[a-z][\s\S]*>/i.test(text) && !text.includes('> El ') && !text.includes('> De:')
}

/**
 * Limpia y formatea contenido de email
 */
export function parseEmailContent(text: string | null | undefined): EmailBlock[] {
  if (!text || typeof text !== 'string') return []
  
  const lines = text.split('\n')
  const blocks: EmailBlock[] = []
  let currentBlock: EmailBlock | null = null
  
  for (const rawLine of lines) {
    const line = rawLine.trimEnd()
    
    // Detectar marcadores de imagen [image: ...]
    if (/^\[image:\s*[^\]]+\]$/i.test(line.trim())) {
      if (currentBlock) {
        blocks.push(currentBlock)
        currentBlock = null
      }
      blocks.push({
        type: 'image-marker',
        content: line.trim(),
        level: 0,
      })
      continue
    }
    
    // Detectar separador como "------------------------------"
    if (/^-{10,}$/.test(line.trim())) {
      if (currentBlock) {
        blocks.push(currentBlock)
        currentBlock = null
      }
      blocks.push({
        type: 'separator',
        content: line,
        level: 0,
      })
      continue
    }
    
    // Detectar líneas que empiezan con > (citas)
    const quoteMatch = line.match(/^(>\s*)+/)
    if (quoteMatch) {
      const level = quoteMatch[0].match(/>/g)?.length || 1
      const content = line.replace(/^(>\s*)+/, '').trim()
      
      // Detectar metadatos de email como "De:", "Enviado:", "Para:", "Asunto:"
      if (/^(De|Enviado|Para|Cc|Asunto|Sent|To|From|Subject|Date):/i.test(content)) {
        if (currentBlock) {
          blocks.push(currentBlock)
          currentBlock = null
        }
        blocks.push({
          type: 'header',
          content,
          level,
        })
      } else {
        if (currentBlock && currentBlock.type === 'quote' && currentBlock.level === level) {
          currentBlock.content += '\n' + content
        } else {
          if (currentBlock) blocks.push(currentBlock)
          currentBlock = {
            type: 'quote',
            content,
            level,
          }
        }
      }
      continue
    }
    
    // Detectar firmas de email (líneas cortas con asteriscos)
    if (/^\*[^*]+\*$/.test(line.trim()) && line.trim().length < 60) {
      if (currentBlock) {
        blocks.push(currentBlock)
        currentBlock = null
      }
      blocks.push({
        type: 'signature',
        content: line.trim().replace(/^\*|\*$/g, ''),
        level: 0,
      })
      continue
    }
    
    // Detectar headers de email sin >
    if (/^(De|Enviado|Para|Cc|Asunto|Sent|To|From|Subject|Date):/i.test(line.trim())) {
      if (currentBlock) {
        blocks.push(currentBlock)
        currentBlock = null
      }
      blocks.push({
        type: 'header',
        content: line.trim(),
        level: 0,
      })
      continue
    }
    
    // Texto normal
    if (currentBlock && currentBlock.type === 'text' && currentBlock.level === 0) {
      currentBlock.content += '\n' + line
    } else {
      if (currentBlock) blocks.push(currentBlock)
      currentBlock = {
        type: 'text',
        content: line,
        level: 0,
      }
    }
  }
  
  if (currentBlock) {
    blocks.push(currentBlock)
  }
  
  return blocks
}

/**
 * Formatea el contenido para mostrar en la UI
 */
export function formatEmailDescription(text: string | null | undefined): string {
  if (!text || typeof text !== 'string') {
    return ''
  }

  if (isHtml(text)) {
    // Si es HTML real, lo devolvemos tal cual
    return text
  }
  
  const blocks = parseEmailContent(text)
  let html = ''
  
  for (const block of blocks) {
    switch (block.type) {
      case 'text':
        html += `<p class="mb-2 text-gray-700">${escapeHtml(block.content).replace(/\n/g, '<br/>')}</p>`
        break
        
      case 'quote':
        html += `<blockquote>${escapeHtml(block.content).replace(/\n/g, '<br/>')}</blockquote>`
        break
        
      case 'header': {
        const parts = block.content.split(':')
        const key = parts[0]
        const value = parts.slice(1).join(':')
        html += `<div class="email-header"><strong>${escapeHtml(key)}:</strong>${escapeHtml(value)}</div>`
        break
      }
        
      case 'separator':
        html += `<hr/>`
        break
        
      case 'image-marker':
        html += `<div class="email-image-marker">[Imagen adjunta]</div>`
        break
        
      case 'signature':
        html += `<div class="email-signature">${escapeHtml(block.content)}</div>`
        break
    }
  }
  
  return html
}

function escapeHtml(text: string | null | undefined): string {
  if (!text || typeof text !== 'string') return ''
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

/**
 * Versión para el resumen truncado (quita citas y formato)
 */
export function getPlainTextSummary(text: string | null | undefined, maxLength: number = 150): string {
  if (!text || typeof text !== 'string') return ''
  
  // Si es HTML, extraer texto
  if (isHtml(text)) {
    const div = document.createElement('div')
    div.innerHTML = text
    text = div.textContent || div.innerText || ''
  }
  
  // Quitar líneas de citas (empiezan con >)
  const lines = text.split('\n')
  const nonQuoteLines = lines.filter(line => !line.trim().startsWith('>'))
  text = nonQuoteLines.join(' ')
  
  // Quitar marcadores de imagen
  text = text.replace(/\[image:[^\]]+\]/gi, '')
  
  // Limpiar espacios múltiples
  text = text.replace(/\s+/g, ' ').trim()
  
  // Truncar
  if (text.length > maxLength) {
    text = text.substring(0, maxLength) + '...'
  }
  
  return text
}